/**
 * Builds a commentMap (Map<string, CommentMapEntry>) from useCommentState's
 * flat comments + fluencyByKey objects.
 *
 * useCommentState stores:
 *   HOMEROOM key: `${studentId}_${gradingCriteriaId}`
 *   GROUPS key:   `${groupId}-${studentId}_${gradingCriteriaId}`
 *
 * buildCommentPayload expects one entry per student+criteria. GROUPS may hydrate
 * the same comment under multiple group-prefixed keys; those are collapsed here
 * so a stale sibling key cannot emit a second conflicting delta.
 *
 * @param {Object} params.comments - from useCommentState
 * @param {Object} [params.fluencyByKey] - readingFluency WPM keyed like comments
 * @returns {Map<string, Object>} keyed by `${gradingProfileStudentId}_${gradingCriteria}`
 */

/**
 * @param {string} compositeKey
 * @returns {{ studentId: number, gradingCriteria: number }}
 */
export const parseCommentCompositeKey = (compositeKey) => {
    // Key format: `${[groupId-]studentId}_${gradingCriteriaId}`
    const underscoreIdx = compositeKey.lastIndexOf("_");
    const studentPart = compositeKey.slice(0, underscoreIdx);
    const gradingCriteria = Number(compositeKey.slice(underscoreIdx + 1));
    const studentId = studentPart.includes("-") ? Number(studentPart.split("-").pop()) : Number(studentPart);
    return { studentId, gradingCriteria };
};

const buildCommentMap = ({ comments, fluencyByKey = {} }) => {
    const commentMap = new Map();
    const keys = new Set([...Object.keys(comments || {}), ...Object.keys(fluencyByKey || {})]);

    keys.forEach((compositeKey) => {
        const { studentId, gradingCriteria } = parseCommentCompositeKey(compositeKey);
        if (!Number.isFinite(studentId) || !Number.isFinite(gradingCriteria)) return;

        const naturalKey = `${studentId}_${gradingCriteria}`;
        const prev = commentMap.get(naturalKey);
        const hasComment = Object.prototype.hasOwnProperty.call(comments || {}, compositeKey);
        const hasFluency = Object.prototype.hasOwnProperty.call(fluencyByKey || {}, compositeKey);

        commentMap.set(naturalKey, {
            gradingProfileStudentId: studentId,
            gradingCriteria,
            comment: hasComment ? (comments[compositeKey] ?? "") : (prev?.comment ?? ""),
            readingFluency: hasFluency ? (fluencyByKey[compositeKey] ?? "") : (prev?.readingFluency ?? "")
        });
    });

    return commentMap;
};

export default buildCommentMap;
